import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Sprout, FlaskConical, Factory, ChefHat, Store, Landmark, Heart,
  ArrowRight, ArrowUpRight, TrendingUp, Globe2, CalendarDays, BookOpen, Database, Globe, Users, Handshake,
} from "lucide-react";
import { FadeIn, MaskedLine, Kicker, SectionShell, EASE } from "../components/Reveal";
import NetworkMap from "../components/NetworkMap";
import JoinSection from "../components/JoinSection";
import { useI18n, LANGS } from "../lib/i18n";
import { fetchPublicDatabase, fetchPublicStats } from "../lib/api";
import SEO from "../components/SEO";

const IMG = {
  field: "https://images.unsplash.com/photo-1781783310656-d376735483d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxyaHViYXJiJTIwZmFybSUyMGZpZWxkfGVufDB8fHx8MTc4NzMwNDMzOHww&ixlib=rb-4.1.0&q=85",
  harvest: "https://images.pexels.com/photos/25365283/pexels-photo-25365283.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  culinary: "https://images.pexels.com/photos/31930001/pexels-photo-31930001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  science: "https://images.unsplash.com/photo-1475906089153-644d9452ce87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxhZ3JpY3VsdHVyZSUyMHNjaWVudGlzdCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzg3MzA0MzY2fDA&ixlib=rb-4.1.0&q=85",
};

const PILLAR_ICONS = [Users, TrendingUp, Sprout, Database, Handshake, CalendarDays];
const WHYJOIN_ICONS = [Globe2, Landmark, Users, BookOpen, CalendarDays, Handshake];
const WHO_ICONS = [Sprout, FlaskConical, Factory, ChefHat, Store, Landmark, Heart];
const MARKET_IMGS = ["culinary", "harvest", "field", "science"];
const ROMAN = ["I", "II", "III"];

function LanguageBar() {
  const { lang, setLang } = useI18n();
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
      className="inline-flex flex-wrap items-center gap-1 border border-border bg-card/70 px-2 py-1.5 backdrop-blur-sm"
      data-testid="landing-language-switcher"
      role="group"
      aria-label="Language"
    >
      <Globe size={13} className="mx-1 text-muted-foreground" />
      <span className="px-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Language · 言語 · Język</span>
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`landing-lang-${l.code}-button`}
          aria-label={l.name}
          className={`px-3 py-1.5 font-mono text-[11px] tracking-[0.05em] transition-colors ${lang === l.code ? "bg-crimson text-parchment" : "text-muted-foreground hover:text-foreground"}`}
        >
          {l.name}
        </button>
      ))}
    </motion.div>
  );
}

function Hero() {
  const { d } = useI18n();
  const h = d.hero;
  const [live, setLive] = useState(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  useEffect(() => {
    fetchPublicStats().then((r) => setLive(r.data)).catch(() => setLive({}));
  }, []);

  const tiles = [
    ...h.stats,
    [h.statMembers, live === null ? "…" : live.members ?? "—"],
    [h.statCountries, live === null ? "…" : live.countries ?? "—"],
  ];

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-28" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-10 rotate-[24deg] bg-gradient-to-b from-stalk/40 via-crimson/25 to-foliage/20 blur-[2px]" />
      <div className="pointer-events-none absolute -right-16 bottom-16 h-96 w-12 -rotate-[18deg] bg-gradient-to-b from-crimson/30 via-stalk/20 to-foliage/25 blur-[2px]" />

      <div className="mx-auto grid w-full max-w-7xl items-center gap-16 px-6 sm:px-10 lg:grid-cols-[1.15fr_0.85fr] lg:px-16">
        <div>
          <LanguageBar />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.25 }} className="mt-6">
            <Kicker>{h.kicker}</Kicker>
          </motion.div>
          <h1 className="mt-8 font-display text-[12vw] font-bold leading-[0.95] tracking-tight sm:text-7xl lg:text-[5.4rem]" data-testid="hero-headline">
            <MaskedLine delay={0.35}>{h.h1a}</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="font-serif font-medium italic text-crimson dark:text-stalk">{h.h1b}</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>{h.h1c}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-8 max-w-lg text-lg leading-relaxed text-muted-foreground"
          >
            {h.para}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#join"
              data-testid="hero-join-cta"
              className="group flex items-center gap-3 border-2 border-crimson bg-crimson px-9 py-5 font-mono text-sm font-medium uppercase tracking-[0.25em] text-parchment shadow-[6px_6px_0_0_rgba(155,17,56,0.25)] transition-all hover:-translate-y-0.5 hover:bg-crimson-dark hover:shadow-[8px_8px_0_0_rgba(155,17,56,0.3)]"
            >
              {h.ctaJoin}
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#pillars"
              data-testid="hero-activities-cta"
              className="group flex items-center gap-3 border border-foreground/25 px-8 py-[18px] font-mono text-xs uppercase tracking-[0.25em] transition-colors hover:border-crimson hover:text-crimson"
            >
              {h.ctaActivities}
              <ArrowRight size={15} className="rotate-90 transition-transform group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-14 grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-4"
            data-testid="hero-honest-stats"
          >
            {tiles.map(([k, v]) => (
              <div key={k} className="bg-background px-5 py-4">
                <p className="font-serif text-3xl">{v}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{k}</p>
              </div>
            ))}
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {h.statsNote}
          </motion.p>
        </div>

        <motion.div style={{ y, rotate }} className="relative hidden lg:block" data-testid="hero-image-frame">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.6, duration: 0.8, ease: EASE }}
            className="absolute -left-10 -top-10 z-10 flex h-28 w-28 items-center justify-center rounded-full border border-border bg-background/95"
            data-testid="hero-rotating-badge"
          >
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-spin-slow">
              <defs>
                <path id="badge-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
              </defs>
              <text style={{ fontSize: "7.4px", letterSpacing: "2.1px" }} className="fill-foreground font-mono uppercase">
                <textPath href="#badge-circle">World Rhubarb Association · Est. 2026 ·</textPath>
              </text>
            </svg>
            <span className="h-2.5 w-2.5 rounded-full bg-crimson" />
          </motion.div>
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.6, ease: EASE }}
            className="specimen-frame relative overflow-hidden rounded-t-[12rem] border border-foreground/15"
          >
            <img src={IMG.field} alt="Rhubarb field cultivation" className="aspect-[3/4] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-moss/40 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="absolute -bottom-5 -left-5 border border-border bg-background px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em]"
          >
            {h.specimen}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const { d } = useI18n();
  const row = [...d.marquee, ...d.marquee];
  return (
    <div className="overflow-hidden border-y border-border bg-moss py-4 dark:bg-nightcard" data-testid="editorial-marquee">
      <div className="flex w-max animate-marquee items-center gap-10">
        {[...row, ...row].map((t, i) => (
          <span key={i} className="flex items-center gap-10 whitespace-nowrap font-serif text-sm italic text-parchment/85">
            {t}
            <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold/70" />
          </span>
        ))}
      </div>
    </div>
  );
}

function PublicDirectory() {
  const { d } = useI18n();
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetchPublicDatabase().then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, []);

  const labels = d.survey.fieldLabels;

  return (
    <div className="mt-16" data-testid="public-directory">
      <FadeIn>
        <div className="flex flex-wrap items-baseline gap-4">
          <Kicker>{d.database.dirKicker}</Kicker>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{d.database.dirTitle}</span>
        </div>
      </FadeIn>
      {entries === null ? null : entries.length === 0 ? (
        <FadeIn delay={0.1}>
          <p className="mt-6 max-w-xl border border-border bg-card/40 p-6 text-sm leading-relaxed text-muted-foreground" data-testid="public-directory-empty">
            {d.database.dirEmpty}
          </p>
        </FadeIn>
      ) : (
        <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" data-testid="public-directory-grid">
          {entries.map((e, i) => (
            <FadeIn key={i} delay={i * 0.06} className="bg-background p-6" data-testid={`public-entry-${i}`}>
              {e.farm_name && <h3 className="font-serif text-xl">{e.farm_name}</h3>}
              <dl className="mt-3 space-y-1.5 text-sm">
                {["org_type", "country", "region", "varieties", "cultivation_area", "annual_production", "website"].map((f) =>
                  e[f] ? (
                    <div key={f} className="flex gap-3">
                      <dt className="w-28 shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{labels[f]}</dt>
                      <dd className="text-foreground/85">
                        {f === "website" ? (
                          <a href={e[f].startsWith("http") ? e[f] : `https://${e[f]}`} target="_blank" rel="noreferrer" className="u-link text-crimson dark:text-stalk">{e[f]}</a>
                        ) : (
                          e[f]
                        )}
                      </dd>
                    </div>
                  ) : null
                )}
              </dl>
            </FadeIn>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { d } = useI18n();

  return (
    <main>
      <SEO page="home" />
      <Hero />
      <Marquee />

      <SectionShell id="pillars">
        <FadeIn><Kicker>{d.pillars.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.pillars.title}</h2>
        </FadeIn>
        <div className="mt-16 grid gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
          {d.pillars.items.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <FadeIn key={p.title} delay={i * 0.12} className="group relative bg-background p-8 transition-colors hover:bg-card lg:p-10" data-testid={`pillar-card-${i}`}>
                <div className="flex items-start justify-between">
                  <Icon size={26} strokeWidth={1.5} className="text-crimson dark:text-stalk" />
                  <span className="font-serif text-5xl italic text-foreground/10 transition-colors group-hover:text-crimson/30">{`0${i + 1}`}</span>
                </div>
                <h3 className="mt-8 font-display text-xl font-semibold uppercase tracking-tight">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span key={t} className="border border-border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{t}</span>
                  ))}
                </div>
              </FadeIn>
            );
          })}
        </div>
      </SectionShell>

      <SectionShell id="network" className="border-t border-border bg-card/40">
        <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <FadeIn><Kicker>{d.network.kicker}</Kicker></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                {d.network.titleA}<span className="italic text-crimson dark:text-stalk">{d.network.titleAccent}</span>
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{d.network.para}</p>
          </FadeIn>
        </div>
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {d.network.stages.map((s, i) => (
            <FadeIn key={s.stage} delay={i * 0.1} className="bg-background p-7" data-testid={`network-stage-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${s.state === "active" ? "bg-foliage" : s.state === "building" ? "bg-gold" : "bg-foreground/20"}`} />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                  {d.network.stateLabels[s.state]}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{s.stage}</h3>
              <p className="mt-1.5 text-xs text-muted-foreground">{s.note}</p>
            </FadeIn>
          ))}
        </div>
        <div className="mt-20">
          <FadeIn><Kicker className="mb-10">{d.network.mapKicker}</Kicker></FadeIn>
          <NetworkMap />
        </div>
      </SectionShell>

      <SectionShell id="manifesto">
        <FadeIn><Kicker>{d.manifesto.kicker}</Kicker></FadeIn>
        <div className="mt-14 space-y-0">
          {d.manifesto.chapters.map((c, i) => (
            <FadeIn key={c.title} delay={i * 0.08} className="group grid gap-6 border-t border-border py-12 last:border-b md:grid-cols-[120px_1fr_1.4fr] md:items-start" data-testid={`manifesto-chapter-${i}`}>
              <span className="font-serif text-6xl italic text-foreground/15 transition-colors duration-500 group-hover:text-crimson md:text-7xl">{ROMAN[i]}</span>
              <h3 className="font-serif text-2xl leading-snug tracking-tight sm:text-3xl">{c.title}</h3>
              <p className="max-w-xl text-base leading-relaxed text-muted-foreground">{c.text}</p>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="mission" className="border-t border-border bg-moss text-parchment dark:bg-nightcard">
        <FadeIn><Kicker className="text-gold">{d.mission.kicker}</Kicker></FadeIn>
        <div className="mt-12 grid gap-12 lg:grid-cols-2">
          <FadeIn delay={0.1}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-parchment/60">{d.mission.visionLabel}</p>
            <p className="mt-5 font-serif text-2xl leading-snug sm:text-3xl" data-testid="vision-statement">{d.mission.vision}</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-parchment/60">{d.mission.missionLabel}</p>
            <ul className="mt-5 space-y-4 text-base leading-relaxed text-parchment/85" data-testid="mission-statement">
              {d.mission.items.map((m) => (
                <li key={m} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold" />{m}</li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </SectionShell>

      <SectionShell id="who">
        <FadeIn><Kicker>{d.who.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.who.title}</h2>
        </FadeIn>
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {d.who.items.map((w, i) => {
            const Icon = WHO_ICONS[i];
            return (
              <FadeIn key={w.title} delay={(i % 4) * 0.08} className="group bg-background p-7 transition-colors hover:bg-card" data-testid={`who-card-${i}`}>
                <Icon size={24} strokeWidth={1.5} className="text-foliage transition-colors group-hover:text-crimson dark:text-stalk" />
                <h3 className="mt-5 font-display text-base font-semibold uppercase tracking-wide">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
              </FadeIn>
            );
          })}
          <FadeIn delay={0.24} className="flex flex-col justify-between bg-crimson p-7 text-parchment">
            <p className="font-serif text-xl italic leading-snug">{d.who.ctaCard}</p>
            <a href="#join" data-testid="who-join-cta" className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] underline-offset-4 hover:underline">
              {d.who.cta} <ArrowRight size={14} />
            </a>
          </FadeIn>
        </div>
      </SectionShell>

      <SectionShell id="membership" className="border-t border-border bg-card/40">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <FadeIn><Kicker>{d.membership.kicker}</Kicker></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
                {d.membership.titleA}<span className="italic text-gold">{d.membership.titleAccent}</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground">{d.membership.para}</p>
            </FadeIn>
            <div className="mt-8 space-y-0 border border-border">
              {d.membership.principles.map((pr, i) => (
                <FadeIn key={pr.t} delay={i * 0.08} className="border-b border-border bg-background p-6 last:border-0" data-testid={`membership-principle-${i}`}>
                  <h3 className="font-display text-sm font-semibold uppercase tracking-wide">{pr.t}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{pr.d}</p>
                </FadeIn>
              ))}
            </div>
          </div>
          <FadeIn delay={0.15} className="relative">
            <div className="specimen-frame overflow-hidden border border-foreground/15">
              <img src={IMG.harvest} alt={d.market.items[1].alt} className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{d.membership.specimen}</p>
          </FadeIn>
        </div>
      </SectionShell>

      <SectionShell id="market">
        <FadeIn><Kicker>{d.market.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {d.market.titleA}<span className="italic text-crimson dark:text-stalk">{d.market.titleAccent}</span>
          </h2>
        </FadeIn>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {d.market.items.map((m, i) => (
            <FadeIn key={m.title} delay={i * 0.08} className="group border border-border bg-background" data-testid={`market-workstream-${i}`}>
              <div className="relative h-48 overflow-hidden">
                <img src={IMG[MARKET_IMGS[i]]} alt={m.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                <span className="absolute bottom-4 left-5 font-serif text-5xl italic text-parchment">{String.fromCharCode(65 + i)}</span>
              </div>
              <div className="p-7">
                <h3 className="font-display text-lg font-semibold uppercase tracking-wide">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="international" className="border-t border-border bg-card/40">
        <FadeIn><Kicker>{d.international.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.international.title}</h2>
        </FadeIn>
        <div className="mt-14 grid gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-5">
          {d.international.items.map((item, i) => (
            <FadeIn key={item.t} delay={i * 0.08} className="bg-background p-6" data-testid={`network-activity-${i}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson dark:text-stalk">{String.fromCharCode(65 + i)}</span>
              <h3 className="mt-3 font-display text-base font-semibold">{item.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.d}</p>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="wrd" className="border-t border-border">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <FadeIn>
            <div className="flex flex-wrap items-center gap-3">
              <Kicker>{d.wrd.kicker}</Kicker>
              <span className="border border-gold/60 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold" data-testid="wrd-status-badge">{d.wrd.badge}</span>
            </div>
            <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              {d.wrd.titleA}<span className="italic text-crimson dark:text-stalk">{d.wrd.titleAccent}</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground">{d.wrd.para}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/world-rhubarb-day" data-testid="wrd-explore-cta" className="group flex items-center gap-3 border border-crimson bg-crimson px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson">
                {d.wrd.cta1} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/world-rhubarb-day#support" data-testid="wrd-support-cta" className="u-link self-center font-mono text-xs uppercase tracking-[0.25em] text-foreground/80 hover:text-crimson">
                {d.wrd.cta2}
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="border border-border">
              {d.wrd.phases.map((ph, i) => (
                <div key={ph.p} className="flex gap-6 border-b border-border p-6 last:border-0" data-testid={`wrd-phase-${i}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{ph.p}</span>
                  <div>
                    <h3 className="font-display text-base font-semibold">{ph.t}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{ph.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </SectionShell>

      <SectionShell id="knowledge" className="border-t border-border bg-card/40">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <FadeIn><Kicker>{d.knowledge.kicker}</Kicker></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
                {d.knowledge.titleA}<span className="italic text-crimson dark:text-stalk">{d.knowledge.titleAccent}</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">{d.knowledge.para}</p>
            </FadeIn>
            <FadeIn delay={0.25}>
              <div className="mt-8 border border-border bg-background p-6">
                <div className="flex items-center gap-3">
                  <BookOpen size={18} className="text-crimson dark:text-stalk" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{d.knowledge.statusLabel}</p>
                </div>
                <p className="mt-3 text-sm text-foreground/80">{d.knowledge.statusText}</p>
              </div>
            </FadeIn>
          </div>
          <div className="grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-3" data-testid="knowledge-category-grid">
            {d.knowledge.categories.map((k, i) => (
              <FadeIn key={k} delay={i * 0.05} className="group bg-background p-6 transition-colors hover:bg-crimson" data-testid={`knowledge-category-${i}`}>
                <p className="font-serif text-lg italic transition-colors group-hover:text-parchment">{k}</p>
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground transition-colors group-hover:text-parchment/70">{d.knowledge.catNote}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="database">
        <FadeIn><Kicker>{d.database.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {d.database.titleA}<span className="italic text-crimson dark:text-stalk">{d.database.titleAccent}</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">{d.database.para}</p>
        </FadeIn>
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4" data-testid="database-grid">
          {d.database.items.map((db, i) => (
            <FadeIn key={db.name} delay={i * 0.05} className="bg-background p-6" data-testid={`database-category-${i}`}>
              <div className="flex items-center justify-between">
                <Database size={18} className="text-foliage dark:text-stalk" />
                <span className="border border-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{d.database.badge}</span>
              </div>
              <h3 className="mt-4 font-display text-base font-semibold">{db.name}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{db.desc}</p>
            </FadeIn>
          ))}
        </div>
        <PublicDirectory />
      </SectionShell>

      <SectionShell id="roadmap" className="border-t border-border bg-card/40">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <FadeIn><Kicker>{d.roadmap.kicker}</Kicker></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.roadmap.title}</h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.15}>
            <span className="border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="roadmap-disclaimer">{d.roadmap.disclaimer}</span>
          </FadeIn>
        </div>
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-5">
          {d.roadmap.items.map((r, i) => (
            <FadeIn key={r.year} delay={i * 0.08} className={`relative p-7 ${r.active ? "bg-crimson text-parchment" : "bg-background"}`} data-testid={`roadmap-year-${r.year}`}>
              <p className={`font-serif text-4xl italic ${r.active ? "text-parchment" : "text-crimson dark:text-stalk"}`}>{r.year}</p>
              <h3 className="mt-3 font-display text-sm font-semibold uppercase tracking-wide">{r.title}</h3>
              <ul className={`mt-4 space-y-2 text-sm ${r.active ? "text-parchment/85" : "text-muted-foreground"}`}>
                {r.items.map((it) => (
                  <li key={it} className="flex gap-2">
                    <span className={`mt-2 h-1 w-1 shrink-0 ${r.active ? "bg-gold" : "bg-crimson dark:bg-stalk"}`} />
                    {it}
                  </li>
                ))}
              </ul>
              {r.active && <span className="absolute right-4 top-4 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">{d.roadmap.now}</span>}
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="why-join" className="border-t border-border">
        <FadeIn><Kicker>{d.whyJoin.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.whyJoin.title}</h2>
        </FadeIn>
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" data-testid="why-join-grid">
          {d.whyJoin.items.map((b, i) => {
            const Icon = WHYJOIN_ICONS[i];
            return (
              <FadeIn key={b.title} delay={(i % 3) * 0.08} className="group bg-background p-7 transition-colors hover:bg-card" data-testid={`why-join-card-${i}`}>
                <Icon size={24} strokeWidth={1.5} className="text-foliage transition-colors group-hover:text-crimson dark:text-stalk" />
                <h3 className="mt-5 font-display text-base font-semibold">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
              </FadeIn>
            );
          })}
          <FadeIn delay={0.24} className="flex flex-col justify-between bg-crimson p-7 text-parchment sm:col-span-2 lg:col-span-3 lg:flex-row lg:items-center lg:gap-10">
            <p className="max-w-2xl font-serif text-lg italic leading-snug">{d.whyJoin.note}</p>
            <a href="#join" data-testid="why-join-cta" className="mt-6 flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] underline-offset-4 hover:underline lg:mt-0">
              {d.whyJoin.cta} <ArrowRight size={14} />
            </a>
          </FadeIn>
        </div>
      </SectionShell>

      <JoinSection />
    </main>
  );
}
